import { timeToBlock } from './helper';
import {
  FULL_DAY_TABLE_BLOCK_END,
  FULL_DAY_TABLE_BLOCK_START,
} from './tableBlocks';

export type SharedTableCapacityItem = {
  id?: string | null;
  guests?: number | null;
  tables?: string[] | null;
  from?: number | null;
  till?: number | null;
  startTimeInBlocks?: number | null;
  endTimeInBlocks?: number | null;
  time?: string | null;
  reservationLength?: number | null;
};

export type SharedTablePoolTable = {
  name: string;
  max: number;
  shared?: boolean;
};

/**
 * Floor-plan tables plus the day's reservations. Needed to pool seats across
 * the tables of one multi-table reservation; see `useSharedTablePool`.
 */
export type SharedTablePool = {
  tables: SharedTablePoolTable[];
  reservations: SharedTableCapacityItem[];
};

type SharedTableCapacityOptions = {
  start: number | null | undefined;
  end: number | null | undefined;
  requestedGuests?: number | null;
  tableMax?: number | null;
  tableName?: string | null;
  id?: string | null;
  pool?: SharedTablePool | null;
};

const asNumber = (value: number | null | undefined) =>
  typeof value === 'number' && !Number.isNaN(value) ? value : null;

/** A block from the reservation book is an entry without guests. */
const isTableBlock = (item: SharedTableCapacityItem) =>
  !((item.guests ?? 0) > 0);

const getItemStart = (item: SharedTableCapacityItem) => {
  const from = asNumber(item.from);
  if (from !== null) return from;

  const startTimeInBlocks = asNumber(item.startTimeInBlocks);
  if (startTimeInBlocks !== null) return startTimeInBlocks;

  return item.time ? timeToBlock(item.time) : null;
};

const getItemEnd = (item: SharedTableCapacityItem, start: number) => {
  const till = asNumber(item.till);
  if (till !== null) return till <= start ? till + 96 : till;

  const endTimeInBlocks = asNumber(item.endTimeInBlocks);
  if (endTimeInBlocks !== null) {
    return endTimeInBlocks <= start ? endTimeInBlocks + 96 : endTimeInBlocks;
  }

  const reservationLength = asNumber(item.reservationLength);
  return reservationLength !== null ? start + reservationLength : null;
};

/**
 * True when a reservation pools seats instead of physically joining tables,
 * i.e. it sits on shared tables only. Without pool data we cannot tell, so a
 * multi-table reservation stays exclusive.
 */
const poolsSeats = (
  item: SharedTableCapacityItem,
  pool?: SharedTablePool | null,
) => {
  const names = item.tables ?? [];
  if (names.length <= 1) return true;
  if (!pool?.tables?.length) return false;

  return names.every(
    (name) => !!pool.tables.find((t) => t.name === name)?.shared,
  );
};

/**
 * Seats taken and seats available for [start, end), both counted over the same
 * pool: this table plus the member tables of every overlapping reservation that
 * sits on shared tables only. So while an 8-pax group uses two 6-seat shared
 * tables, the pool holds 12 seats with 8 taken and 4 stay bookable. A
 * reservation that joins tables exclusively keeps taking every table whole.
 */
export const getSharedTableUsage = (
  items: SharedTableCapacityItem[],
  options: SharedTableCapacityOptions,
) => {
  const start = asNumber(options.start);
  const rawEnd = asNumber(options.end);
  const tableMax = Math.max(0, options.tableMax ?? 0);
  if (start === null || rawEnd === null) return { used: 0, max: tableMax };

  const end = rawEnd <= start ? rawEnd + 96 : rawEnd;

  const itemWindow = (item: SharedTableCapacityItem) => {
    const itemStart = getItemStart(item);
    const itemEnd = itemStart === null ? null : getItemEnd(item, itemStart);
    if (
      itemStart === null ||
      itemEnd === null ||
      !Number.isFinite(itemStart) ||
      !Number.isFinite(itemEnd)
    ) {
      // Like tableBlockCoversWindow, a block without a readable window covers.
      return isTableBlock(item) ? { start, end, fullDay: true } : null;
    }

    // Reads turn a full-day block's 00:00 into block 96, so detect it by span
    // like isFullDayTableBlock and let it cover the whole checked window.
    const fullDay =
      isTableBlock(item) &&
      itemEnd - itemStart >=
        FULL_DAY_TABLE_BLOCK_END - FULL_DAY_TABLE_BLOCK_START;

    return fullDay
      ? { start, end, fullDay }
      : { start: itemStart, end: itemEnd, fullDay };
  };

  // Extra guests on a blocked table count as new guests.
  const self = options.id
    ? (options.pool?.reservations ?? items).find(
        (r) =>
          r.id === options.id &&
          (!options.tableName || !!r.tables?.includes(options.tableName)) &&
          !((options.requestedGuests ?? 0) > (r.guests ?? 0)),
      )
    : undefined;
  const selfWindow = self ? itemWindow(self) : null;

  const overlapWindow = (item: SharedTableCapacityItem) => {
    if (item.id && item.id === options.id) return null;

    const range = itemWindow(item);
    if (!range || range.end <= start || range.start >= end) return null;

    // A block closes a shared table to everyone except a reservation already
    // seated on the checked table inside the blocked window whose checked
    // window still overlaps its stay without moving deeper into the block,
    // same rule as the availability engine.
    if (
      isTableBlock(item) &&
      selfWindow &&
      start < selfWindow.end &&
      end > selfWindow.start &&
      (range.fullDay ||
        (selfWindow.end > range.start &&
          selfWindow.start < range.end &&
          (range.start <= selfWindow.start || start <= selfWindow.start) &&
          (range.end >= selfWindow.end || start >= selfWindow.start)))
    ) {
      return null;
    }

    return range;
  };

  const poolNames = new Set<string>(
    options.tableName ? [options.tableName] : [],
  );
  items.forEach((item) => {
    if (!overlapWindow(item)) return;
    if ((item.tables?.length ?? 1) <= 1 || !poolsSeats(item, options.pool)) {
      return;
    }
    item.tables?.forEach((name) => poolNames.add(name));
  });

  // Follow pooled reservations transitively: one on [A, B] plus an
  // overlapping one on [B, C] pools all three tables, so C's seats count
  // toward the same capacity its guests are drawn from.
  const poolSource = options.pool?.reservations ?? items;
  let grew = poolNames.size > 0;
  while (grew) {
    grew = false;
    for (const item of poolSource) {
      if ((item.tables?.length ?? 1) <= 1 || !poolsSeats(item, options.pool)) {
        continue;
      }
      if (!overlapWindow(item)) continue;
      if (!item.tables?.some((name) => poolNames.has(name))) continue;
      for (const name of item.tables ?? []) {
        if (!poolNames.has(name)) {
          poolNames.add(name);
          grew = true;
        }
      }
    }
  }

  const poolTables = Array.from(poolNames).map(
    (name) =>
      options.pool?.tables.find((t) => t.name === name) ?? {
        name,
        max: tableMax,
      },
  );
  const max = poolTables.reduce((acc, t) => acc + (t.max || 0), 0) || tableMax;

  // The pool list also holds the parties on the other pool members and the
  // blocks, which callers filter out of `items`, so it wins whenever it is
  // there.
  const relevant = options.pool?.reservations
    ? options.pool.reservations.filter((r) =>
        r.tables?.some((name) => poolNames.has(name)),
      )
    : items;

  // A table block carries no guests and takes its tables whole.
  const seats = (item: SharedTableCapacityItem) => {
    if (!isTableBlock(item) && poolsSeats(item, options.pool)) {
      return item.guests ?? 0;
    }

    return (
      poolTables
        .filter((t) => item.tables?.includes(t.name))
        .reduce((acc, t) => acc + (t.max || 0), 0) || (item.guests ?? 0)
    );
  };

  // A reservation on several tables shows up once per table, so dedupe by id.
  const seen = new Set<string>();
  const windows: {
    start: number;
    end: number;
    seats: number;
    tableBlock: boolean;
    closesTable: boolean;
  }[] = [];
  relevant.forEach((item) => {
    const range = overlapWindow(item);
    if (!range) return;
    if (item.id) {
      if (seen.has(item.id)) return;
      seen.add(item.id);
    }
    windows.push({
      start: range.start,
      end: range.end,
      seats: seats(item),
      tableBlock: isTableBlock(item),
      closesTable:
        isTableBlock(item) &&
        !!options.tableName &&
        !!item.tables?.includes(options.tableName),
    });
  });

  let used = 0;
  for (let block = start; block < end; block++) {
    let guestSeats = 0;
    let blockedSeats = 0;
    let closed = false;
    windows.forEach((w) => {
      if (w.start > block || block >= w.end) return;
      if (w.closesTable) closed = true;
      if (w.tableBlock) blockedSeats += w.seats;
      else guestSeats += w.seats;
    });
    // A block only fills the pool up, real overbooking still shows.
    const usedAtBlock = Math.max(
      guestSeats,
      closed ? max : Math.min(max, guestSeats + blockedSeats),
    );
    if (usedAtBlock > used) used = usedAtBlock;
  }

  return { used, max };
};

export const getSharedTableOverlappingGuests = (
  items: SharedTableCapacityItem[],
  options: SharedTableCapacityOptions,
) => getSharedTableUsage(items, options).used;

export const getSharedTableCapacityInfo = (
  items: SharedTableCapacityItem[],
  options: SharedTableCapacityOptions,
) => {
  const { used, max } = getSharedTableUsage(items, options);
  const requestedGuests = Math.max(0, options.requestedGuests ?? 0);
  const occupiedAfterRequest = used + requestedGuests;

  return {
    overlappingGuests: used,
    requestedGuests,
    max,
    remaining: Math.max(0, max - used),
    occupiedAfterRequest,
    available: occupiedAfterRequest <= max,
  };
};
